import { ofetch, type FetchContext, type FetchOptions } from 'ofetch';
import { defineNuxtPlugin } from '#imports';
import { start, finish } from '~/composables/use-loading';
import { useCartStore } from '~/stores/cart';
import type { NuxtAppCompat } from '@nuxt/bridge-schema';

interface CustomFetchContext extends FetchContext {
  loadingSpinner?: boolean;
}

interface CustomHeaders extends Headers {
  'x-csrf-token'?: string;
}

interface CustomFetchOptions extends FetchOptions {
  Headers?: CustomHeaders;
  onRequest?(context: CustomFetchContext): Promise<void> | void;
}

interface CustomFetchRequestOptions extends FetchOptions {
  loadingSpinner?: boolean;
  noSession?: boolean; // Disable session e.g. for downtimes / redirects,
  headers?: CustomHeaders;
}

// We declare $shopApi as global. We cannot use native $fetch, cause the x-csrf-token won't be handled.
const buildShopApiClient = (nuxtApp: NuxtAppCompat) => {
  const cartStore = useCartStore(nuxtApp.$pinia);

  return ofetch.create({
    headers: {
      Accept: 'application/vnd.td.eg-solo.v1+json',
      'Content-Type': 'application/json',
    },
    onResponse({ response }) {
      const csrfToken = response.headers.get('x-csrf-token');
      if (csrfToken) {
        cartStore.setCsrfToken(csrfToken);
      }
      finish();
    },

    onRequest({ options }: { options: CustomFetchRequestOptions }) {
      if (options.loadingSpinner !== false) {
        start();
      }
      if (options.noSession !== true && cartStore.csrfToken) {
        if (!options.headers) {
          // eslint-disable-next-line no-param-reassign
          options.headers = new Headers();
        }
        options.headers?.set('x-csrf-token', cartStore.csrfToken);
      }
    },
    onResponseError({ response }) {
      const csrfToken = response?.headers?.get('x-csrf-token');
      if (csrfToken) {
        cartStore.setCsrfToken(csrfToken);
      }
      finish();
    },
    onRequestError(context) {
      finish();
    },
  } as CustomFetchOptions);
};

export default defineNuxtPlugin((nuxtApp) => {
  globalThis.$shopApi = buildShopApiClient(nuxtApp);
});
